import React, { useState, useEffect } from 'react';
import { getDatabase, ref, onValue, off } from 'firebase/database';
import PropTypes from 'prop-types';
import { db } from '..';


/**
 * ChatHistory component fetches and displays chat messages for a specific job.
 * @param {Object} props - Component props.
 * @param {string} props.jobId - The ID of the job to fetch chat messages for.
 * @param {string} props.senderId - The ID of the current user (electrician or customer).
 * @returns {JSX.Element} The rendered chat history component.
 */
const ChatHistory = ({ jobId, senderId }) => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Use the existing Firebase database instance
    

    useEffect(() => {
        console.log(db)
        // Reference to chat messages for the job
        const chatRef = ref(db, `messages/${jobId}`);
        

        // Listen for changes in chat messages
        const onMessage = onValue(
            chatRef,
            (snapshot) => {
                if (snapshot.exists()) {
                    const data = Object.values(snapshot.val()).reverse(); // Latest messages at bottom
                    setMessages(data);
                } else {
                    setMessages([]); // No messages
                }
                setLoading(false);
            },
            (err) => {
                setError(`Failed to fetch messages: ${err.message}`);
                setLoading(false);
            }
        );

        // Cleanup listener on unmount
        return () => off(chatRef, 'value', onMessage);
    }, [jobId, db]);

    /**
     * Formats a date string to a readable format.
     * @param {string} date - The ISO date string.
     * @returns {string} Formatted date or 'N/A'.
     */
    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="container py-4">
            <div className="card shadow-sm border-0">
               
                <div className="card-body p-4" style={{ maxHeight: '600px', overflowY: 'auto', scrollbarWidth:0 }}>
                    {loading && (
                        <div className="text-center">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    )}
                    {error && (
                        <div className="alert alert-danger alert-dismissible fade show" role="alert">
                            {error}
                            <button
                                type="button"
                                className="btn-close"
                                onClick={() => setError(null)}
                                aria-label="Close"
                            ></button>
                        </div>
                    )}
                    {!loading && !error && messages.length === 0 && (
                        <div className="text-center text-muted">No messages available.</div>
                    )}
                    {!loading && !error && messages.length > 0 && (
                        <div className="chat-container">
                            {messages.map((message) => (
                                <div
                                    key={message._id}
                                    className={`d-flex mb-3 ${
                                        message.user._id === senderId
                                            ? 'justify-content-end'
                                            : 'justify-content-start'
                                    }`}
                                >
                                    <div
                                        className={`chat-message p-3 rounded-3 ${
                                            message.user._id === senderId
                                                ? 'bg-primary text-white'
                                                : 'bg-light text-dark'
                                        }`}
                                        style={{ maxWidth: '70%' }}
                                    >
                                        {message.image && (
                                            <img
                                                src={message.image}
                                                alt="Chat image"
                                                className="img-fluid rounded mb-2"
                                                style={{ maxWidth: '150px' }}
                                            />
                                        )}
                                        {message.text && <p className="mb-1">{message.text}</p>}
                                        <small className="d-block text-end opacity-75">
                                            {formatDate(message.createdAt)}
                                        </small>
                                        <small className="d-block text-end">
                                            {message.status === 'delivered' ? (
                                                <span>✓</span>
                                            ) : message.status === 'read' ? (
                                                <span className="text-info">✓✓</span>
                                            ) : (
                                                <span>✓</span>
                                            )}
                                        </small>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

ChatHistory.propTypes = {
    jobId: PropTypes.string.isRequired,
    senderId: PropTypes.string.isRequired
};

export default ChatHistory;